
import Vue from 'vue';
import { mapGetters, mapState, mapActions } from 'vuex';
import ReportingAnalyticsCard from '@/views/Team/components/ReportingAnalyticsCard.vue';
import MetricAnalyticsCard from '@/views/Team/components/MetricAnalyticsCard.vue';
import PrePurchaseRunnitTokensBtn from '@/views/Team/CompanyDashboard/ManageCompanyTokenBalance/PrePurchaseRunnitTokensBtn.vue';
import { DbTimestamp } from '@run-diffusion/shared';
import { TeamsSummary, TeamsSummaryCounts } from '@/utils/teamsApi';
import { TeamUserMixin } from '@/mixins';
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';
import { getMonthlyResetDisplayText } from '@/utils/timeUtils';
import pluralize from 'pluralize';
import { titleCase } from '@/filters';
import { ROUTER } from '@/router/constants';

export default Vue.extend({
	name: 'CompanyMetricsBtns',
	mixins: [
		TeamUserMixin,
	],
	props: {
		// GET /api/v2/internal/teams/summary, camelCased (teamsApi.ts), or null before the first answer
		teamsSummary: { type: Object as () => TeamsSummary, default: null },
		loadingTeamsSummary: { type: Boolean, default: false },
	},
	computed: {
		...mapState([
			'user',
			'team',
			'realTimeMoment',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		// The table under the cards shows the active or the deactivated teams, and the cards count what it shows
		showingDeactivated (): boolean {
			return !!this.$route.query.deactivated;
		},
		// The half of the summary the table shows, or null before the first answer
		summaryCounts (): TeamsSummaryCounts | null {
			if (!this.teamsSummary) return null;
			return this.showingDeactivated ? this.teamsSummary.deactivated : this.teamsSummary.active;
		},
		// The skeleton is for the first answer only; a refetch keeps the numbers on screen
		summaryLoading (): boolean {
			return !this.teamsSummary && this.loadingTeamsSummary;
		},
		// With no answer at all, a dash: a 0 would read as real
		teamsCountText (): string {
			return this.summaryCounts ? String(this.summaryCounts.teamCount) : '\u2013';
		},
		membersCountText (): string {
			return this.summaryCounts ? String(this.summaryCounts.memberCount) : '\u2013';
		},
		// The summary counts every team for a MANAGE_TEAMS holder and only your own otherwise,
		// and says which; the words follow what was counted, not what the client guesses
		summaryCoversOwnTeamsOnly (): boolean {
			return this.teamsSummary?.scope === 'joined';
		},
		summaryScopeWord (): string {
			return this.summaryCoversOwnTeamsOnly ? 'your' : 'all';
		},
		// Standalone card labels, so title-cased (the card renders its label in capitals).
		// The same words as the table's heading beneath, so one set of teams has one name.
		teamsLabel (): string {
			const view: string = this.showingDeactivated ? 'Deactivated' : 'Active';
			return `${this.summaryCoversOwnTeamsOnly ? 'Your ' : ''}${view} ${titleCase(pluralize(this.displayLabels.team))}`;
		},
		teamMembersLabel (): string {
			return `${titleCase(this.displayLabels.team)} Members`;
		},
		currCompany () {
			return this.team && typeof this.team.company !== 'string' ? this.team.company : null;
		},
		paidUsers (): number {
			return (this.companyClubOfferBenefits?.isEnterprise && this.currCompany?.paidUsers) || 0;
		},
		readonlyUsers (): number {
			return (this.companyClubOfferBenefits?.isEnterprise && this.currCompany?.readonlyUsers) || 0;
		},
		resetDisplayText () {
			const renewAt: DbTimestamp = this.currCompany?.monthlyBenefitsRenewAt || null;
			if (!renewAt) return '';
			return getMonthlyResetDisplayText(renewAt, this.realTimeMoment, { isShortened: true });
		},
	},
	methods: {
		...mapActions([
			'setUpsellDialog',
		]),
		onEnterpriseReportsUpsell () {
			this.setUpsellDialog({ noCompanyReports: true });
		},
		onEnterpriseBudgetsUpsell () {
			this.setUpsellDialog({ noCompanyBudgets: true });
		},
		onViewAllMembersClick () {
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.MEMBERS_DIRECTORY,
			});
		},
	},
	components: {
		RunnitTokens,
		ReportingAnalyticsCard,
		MetricAnalyticsCard,
		PrePurchaseRunnitTokensBtn,
	},
});
